import useAppFunctionalityConfig from "hooks/useAppFunctionalityConfig";
import { assertInventoryCurrent, inventoryLocked, preserveApproval } from "utils/inventoryLifecycle";
import { getInventoryEditorSnapshot, saveInventoryEditor, withEditableInventory } from "contexts/inventoryPersistence";
import { getInventoryBuyerApprovalStatus } from "@foresting-tomorrow/converters";
import React, {
  createContext,
  useEffect,
  useReducer,
  useRef,
  useCallback,
} from 'react'
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  onSnapshot,
  deleteDoc,
  writeBatch,
  runTransaction,
  DocumentReference,
} from 'firebase/firestore'
import { firestore as db } from 'lib/firebase/firebase'
import {
  MapDataProps,
  MapDataContextType,
  SaveFeatureProps,
  SaveCutTransactionProps,
  SaveStandDataProps,
  DeleteFeatureProps,
  SaveNoteProps,
  DeleteNoteProps,
  SaveActivityProps,
  CategoriesFilterLogicType,
  SaveTempStandUpdateDataProps,
  saveProjectionDataProps,
} from 'types/mapData'
import { firestoreConverters, includeFeatureInAudience } from '@foresting-tomorrow/converters'

import dayjs from 'dayjs'
import mapDataReducer from 'store/reducers/MapData'

// action - state management
import {
  MAP_DATA_API_CALL_START,
  MAP_DATA_API_CALL_FAIL,
  FETCH_STAND_DATA_SUCCESS,
  SAVE_ACTIVITY_SUCCESS,
  SAVE_FEATURE_SUCCESS,
  DELETE_FEATURE_SUCCESS,
  SAVE_STAND_DATA_SUCCESS,
  SAVE_THINNING_DATA_SUCCESS,
  DELETE_THINNING_DATA_SUCCESS,
  SET_SELECTED_FEATURE_ID,
  SET_SELECTED_FEATURE,
  SET_SELECTED_ACTIVITY_ID,
  SET_REMOVE_FEATURE_ID,
  SET_FEATURE_EDIT_MODE,
  SET_DRAW_MODE,
  SET_META_DATA_EDIT_MODE,
  ADDED_ACTIVITY_SUCCESS,
  MODIFIED_ACTIVITY_SUCCESS,
  REMOVED_ACTIVITY_SUCCESS,
  ADDED_FEATURE_SUCCESS,
  MODIFIED_FEATURE_SUCCESS,
  REMOVED_FEATURE_SUCCESS,
  ADDED_ACTIVITY_FEATURE_SUCCESS,
  MODIFIED_ACTIVITY_FEATURE_SUCCESS,
  REMOVED_ACTIVITY_FEATURE_SUCCESS,
  ADDED_NOTE_SUCCESS,
  MODIFIED_NOTE_SUCCESS,
  REMOVED_NOTE_SUCCESS,
  ADDED_THINNING_DATA_SUCCESS,
  MODIFIED_THINNING_DATA_SUCCESS,
  REMOVED_THINNING_DATA_SUCCESS,
  ADDED_INVENTORY_SUCCESS,
  MODIFIED_INVENTORY_SUCCESS,
  REMOVED_INVENTORY_SUCCESS,
  SET_ESTATE_MAP_FILTER_FEATURE_IDS,
  SET_ESTATE_PROJECT_FILTER_ACTIVITY_IDS,
  SET_ADD_STAND_DATA,
  SET_ADD_MEASUREMENT_DATA,
  SET_USE_PROGRAM_DEFINED_SETTING,
  SET_AREA_BEARING_SETTING,
  MAP_DATA_RESET_STATE,
  SET_CATEGORIES,
  SET_ACTIVE_EXTERNAL_LAYERS_LEGEND,
  SET_TABLE_STATE,
  SET_BOARD_STATE,
  SET_NOTES_LAYER_STATE,
  SET_MAP_CATEGORIES_FILTER_LOGIC_TYPE,
  MAP_DATA_SET_TRIGGER_RERENDER,
  MAP_DATA_API_CALL_SUCCESS,
  ADDED_INVENTORY_MEASUREMENT_SUCCESS,
  MODIFIED_INVENTORY_MEASUREMENT_SUCCESS,
  REMOVED_INVENTORY_MEASUREMENT_SUCCESS,
  SET_LIST_PAGE_TAB_VALUE,
  SET_INVENTORY_FILTER_IDS,
  SET_EXTERNALLY_ACTIVATED_THEMES,
} from 'store/reducers/actions'

import { DEFAULT_ACTIVITY_STATUS_TYPE } from 'config/config-general'

import useEstatesAndMaps from 'hooks/useEstatesAndMaps'
import useAuth from 'hooks/useAuth'
import {
  FirestoreInventoryItemProps,
  MapProjectRoles,
  SaveInventoryItemProps,
  SaveInventoryMeasurementProps,
} from 'types/auth'
import { dataObjectWithoutUndefinedFields } from 'utils/dataManipulation'
import {
  FirestoreActivityProps,
  FirestoreLayerLegend,
  SaveThinningProps,
} from 'types/firestoreFeatures'
import { mixpanelEvents } from 'config/servicesConfig/mixpanelEvents'
import { Geometry } from 'geojson'
import { getDefaultActivityColorId } from 'features/map/components/areaInput/utils/getDefaultFeatureParameters'

// Firestore event listeners
let firestoreEventListeners: any[] = []

const initialState: MapDataProps = {
  loading: false,
  error: null,
  triggerRerender: 0,
  selectedFeatureId: null,
  selectedFeature: null,
  selectedActivityId: null,
  addStandData: true,
  addMeasurementData: true,
  useProgramDefinedSetting: true,
  areaBearingSetting: true,
  editMode: false,
  drawMode: false,
  metaDataEditMode: false,
  lastSavedFeature: { id: null, geometry: null, entityType: null },
  changeFeature: null,
  removeFeatureId: null,
  changeNote: null,
  removeNoteId: null,
  changeInventory: null,
  removeInventoryId: null,
  features: {},
  standData: {},
  thinningData: {},
  activities: {},
  activityFeatures: {},
  inventories: {},
  inventoryMeasurements: {},
  notes: {},
  notesLayerActive: true,
  listPageTabValue: 0,
  tableState: {
    inventory: {
      selectedMapIds: [],
      showColumnFilters: false,
      showFilterResultInMap: false,
      tableType: 'full',
      columnFilters: [],
      columnVisibility: {},
      sorting: [],
      globalFilter: '',
      charts: {
        tabValue: 0,
        estimationParameters: {
          thinningType: 'planned',
          thinningStartAge: 25,
          thinningInterval: 5,
          thinningPercentage: 30,
          sliderValue: 10,
        },
      },
    },
    rawWoodInventory: {
      selectedMapIds: [],
      selectedProjectIds: [],
      showColumnFilters: false,
      showFilterResultInMap: false,
      columnFilters: [],
      columnVisibility: {},
      sorting: [],
      globalFilter: '',
    },
  },
  boardState: {
    projectBoard: {
      selectedProjectIds: [],
      showFilterResultInMap: false,
      globalFilter: '',
      filterDataState: {
        status: [],
        responsible: [],
        groups: [],
        types: [],
        screeningBiomassCategory: [],
        startDate: null,
        endDate: null,
      },
    },
  },
  inventoryFilterIds: [],
  estateMapFilterFeatureIds: {},
  estateProjectFilterActivityIds: {},
  activeExternalLayersLegend: [],
  categories: null,
  categoriesFilterLogicType: 'AND',
  externallyActivatedThemes: [],
}

const MapDataContext = createContext<MapDataContextType | null>(null)

export function MapDataProvider({ children }: { children: React.ReactElement }) {
  const { isAudienceMode } = useAppFunctionalityConfig();
  return isAudienceMode
    ? <AudienceMapDataProvider>{children}</AudienceMapDataProvider>
    : <WorkspaceMapDataProvider>{children}</WorkspaceMapDataProvider>;
}

const emptyAudienceFeatures: Record<string, import("geojson").Feature> = {};

function AudienceMapDataProvider({ children }: { children: React.ReactElement }) {
  const { selectedMapId, audienceFeatures = emptyAudienceFeatures, audienceMapStatus } = useEstatesAndMaps();
  const [state, dispatch] = useReducer(mapDataReducer, { ...initialState, categories: ["audience", "points", "lines", "polygons"], notesLayerActive: false });
  const deny = async () => { throw new Error("audience-read-only"); };
  const noop = () => {};
  const edit = (enabled: boolean) => { if (enabled) throw new Error("audience-read-only"); };
  // Corridor line layers store feature properties in options; GeoJSON point
  // and polygon layers expose them through feature.properties.
  const selectedId = state.selectedFeature?.feature?.properties?.id ?? state.selectedFeature?.options?.id;
  useEffect(() => {
    dispatch({ type: SET_SELECTED_FEATURE, selectedFeature: null, lastSavedFeature: initialState.lastSavedFeature });
    dispatch({ type: SET_SELECTED_FEATURE_ID, selectedFeatureId: null });
  }, [selectedMapId, audienceFeatures]);
  const value: MapDataContextType = {
    ...state, features: selectedMapId ? { [selectedMapId]: audienceFeatures } : {},
    categoriesFilterLogicType: 'AND',
    loading: audienceMapStatus === "loading", selectedFeature: selectedId && audienceFeatures[selectedId] ? state.selectedFeature : null,
    setTriggerRerender: noop,
    setSelectedFeatureId: id => dispatch({ type: SET_SELECTED_FEATURE_ID, selectedFeatureId: id }),
    setSelectedFeature: feature => dispatch({ type: SET_SELECTED_FEATURE, selectedFeature: feature, lastSavedFeature: initialState.lastSavedFeature }),
    setSelectedActivityId: noop, setRemoveFeatureId: noop, setAddStandData: noop, setAddMeasurementData: noop,
    setUseProgramDefinedSetting: noop, setAreaBearingSetting: noop,
    setFeatureEditMode: edit, setDrawMode: edit, setMetaDataEditMode: edit,
    setInventoryFilterIds: noop, setEstateMapFilterFeatureIds: noop, setEstateProjectFilterActivityIds: noop,
    setListPageTabValue: noop, setTableState: noop, setBoardState: noop,
    setCategories: categories => dispatch({ type: SET_CATEGORIES, categories }),
    setMapCategoriesFilterLogicType: noop,
    setActiveExternalLayersLegend: legends => dispatch({ type: SET_ACTIVE_EXTERNAL_LAYERS_LEGEND, activeExternalLayersLegend: legends }),
    setExternallyActivatedThemes: noop, setNotesLayerState: noop,
    fetchActivity: deny, fetchActivities: deny, fetchActivityFeatures: deny, fetchFeatures: deny,
    fetchStandData: deny, fetchThinningData: deny, fetchInventoryMeasurements: deny,
    saveActivity: deny, deleteActivity: deny, updateActivityStatus: deny, updateActivityPriority: deny,
    saveFeature: deny, saveCutTransaction: deny, deleteFeature: deny, saveStandData: deny,
    saveTempStandUpdateData: deny, saveProjectionData: deny, saveThinning: deny, deleteThinning: deny,
    getInventoryEditorSnapshot: deny, saveInventoryEditor: deny, saveInventory: deny, deleteInventory: deny,
    saveInventoryMeasurement: deny, saveNote: deny, deleteNote: deny,
    registerFileHandler: key => key, unregisterFileHandler: noop,
  };
  return <MapDataContext.Provider value={value}>{children}</MapDataContext.Provider>;
}

function WorkspaceMapDataProvider({
  children,
}: {
  children: React.ReactElement
}) {
  // --- Hooks --- //
  const { orgToOrgConsumerSubscriptions, callCloudFunction } = useAuth()
  const { isAudienceMode, assertDomainMutationAllowed } = useAppFunctionalityConfig();
  const workspaceActiveRef = useRef(true);
  useEffect(() => {
    workspaceActiveRef.current = true;
    return () => { workspaceActiveRef.current = false; };
  }, []);
  const [state, dispatch] = useReducer(mapDataReducer, initialState)
  const {
    maps,
    selectedMapId,
    selectedProjectId,
    projects,
    setSelectedMapId,
    setSelectedProjectId,
    fetchTempProjectWithRole,
  } = useEstatesAndMaps()
  const { isLoggedIn, user } = useAuth()

  // registry keyed by provider instance id (string)
  const registry = useRef(
    new Map<string, (activityId: string) => Promise<void>>(),
  )

  const registerFileHandler = useCallback(
    (key: string, handler: (activityId: string) => Promise<void>) => {
      registry.current.set(key, handler)
      return key
    },
    [],
  );

  const unregisterFileHandler = useCallback((key: string) => {
    registry.current.delete(key)
  }, [])

  useEffect(() => {
    if (!isAudienceMode) return;
    dispatch({ type: SET_FEATURE_EDIT_MODE, editMode: false });
    dispatch({ type: SET_DRAW_MODE, drawMode: false });
    dispatch({ type: SET_META_DATA_EDIT_MODE, editMode: false });
  }, [isAudienceMode]);

  // --- State an refs --- //
  const inventoryFetchRef = useRef(false)

  // --- Effects --- //
  // --- Reset state and unsubscribe from firestore event listeners on logout --- //
  useEffect(() => {
    if (!isLoggedIn) resetState();
    return () => {
      firestoreEventListeners.forEach(unsubscribe => unsubscribe());
      firestoreEventListeners = [];
    };
  }, [isLoggedIn]);

  // --- Fetch data --- //
  // Map features
  useEffect(() => {
    if (selectedMapId && !state.features[selectedMapId]) {
      let categories = null
      if (
        maps[selectedMapId] &&
        maps[selectedMapId].roles &&
        user &&
        user.id &&
        maps[selectedMapId].roles![user?.id]
      ) {
        categories =
          maps[selectedMapId].roles![user?.id].categories &&
          maps[selectedMapId].roles![user?.id].categories!.length > 0
            ? maps[selectedMapId].roles![user?.id].categories
            : null
      }
      fetchFeatures(
        selectedMapId,
        "maps",
        user?.authType ? user?.authType : "anonymous",
        categories,
      );
    }
    if (selectedMapId && !state.standData[selectedMapId]) {
      fetchStandData(selectedMapId)
      fetchThinningData(selectedMapId, 'mapId')
    }
    if (selectedMapId && !state.notes[selectedMapId]) {
      if (user && user.authType !== 'anonymous') {
        fetchNotes(selectedMapId)
      }
    }
  }, [selectedMapId])

  // Fetch inventories
  useEffect(() => {
    if (!user || !user.orgId || inventoryFetchRef.current) return
    fetchInventories(user.orgId, 'orgId')
    fetchInventories(user.orgId, 'buyerId')
    inventoryFetchRef.current = true
  }, [user])

  // Project activities
  useEffect(() => {
    if (!user || !user.id || Object.keys(projects).length === 0) return
    if (selectedProjectId === null && state.selectedActivityId) {
      setSelectedActivityId(null)
    }
    if (selectedProjectId && !state.activities[selectedProjectId]) {
      fetchActivities(selectedProjectId)
    }
  }, [selectedProjectId, projects])
  // Activity features
  useEffect(() => {
    if (state.selectedActivityId && state.selectedActivityId !== 'new') {
      if (!state.activityFeatures[state.selectedActivityId]) {
        fetchActivityFeatures(state.selectedActivityId)
      }
      // Check if forest features and stands need to be fetched
      if (
        selectedProjectId &&
        state.activities[selectedProjectId] &&
        state.activities[selectedProjectId][state.selectedActivityId]
      ) {
        const act: FirestoreActivityProps =
          state.activities[selectedProjectId][state.selectedActivityId]
        if (act.mapFeatureRefs && act.mapFeatureRefs.length > 0) {
          act.mapFeatureRefs.forEach((el: string) => {
            const mapId = el.split('/')[0]
            if (mapId && !state.standData[mapId]) {
              fetchStandData(mapId)
              fetchThinningData(mapId, 'mapId')
            }
          })
        }
      }
    }
  }, [state.selectedActivityId])

  // --- App specific functionality --- //
  const resetState = () => {
    dispatch({
      type: MAP_DATA_RESET_STATE,
    })
  }

  const setTriggerRerender = () => {
    dispatch({
      type: MAP_DATA_SET_TRIGGER_RERENDER,
    })
  }

  const setSelectedFeatureId = (id: string | null) => {
    dispatch({
      type: SET_SELECTED_FEATURE_ID,
      selectedFeatureId: id,
    })
  }

  // Use originCaller for debugging purposes to track where setSelectedFeature is called from
  const setSelectedFeature = (feature: null | any, originCaller?: string) => {
    let selFeatId = null
    let selFeatGeometry = null
    let feat = null
    if (feature) {
      feat = feature.feature ? feature.feature : feature.toGeoJSON()
    }
    if (feat?.properties?.id) selFeatId = feature?.feature?.properties?.id
    if (feat?.geometry) selFeatGeometry = feature?.feature?.geometry
    dispatch({
      type: SET_SELECTED_FEATURE,
      selectedFeature: feature,
      lastSavedFeature: { id: selFeatId, geometry: selFeatGeometry, entityType: "feature" },
    })
  }

  const setSelectedActivityId = (id: string | null) => {
    dispatch({
      type: SET_SELECTED_ACTIVITY_ID,
      selectedActivityId: id,
    })
  }

  const setRemoveFeatureId = (id: string | null) => {
    dispatch({
      type: SET_REMOVE_FEATURE_ID,
      removeFeatureId: id,
    })
  }

  const setAddStandData = (state: boolean) => {
    dispatch({
      type: SET_ADD_STAND_DATA,
      addStandData: state,
    })
  }

  const setAddMeasurementData = (state: boolean) => {
    dispatch({
      type: SET_ADD_MEASUREMENT_DATA,
      addMeasurementData: state,
    })
  }

  const setUseProgramDefinedSetting = (state: boolean) => {
    dispatch({
      type: SET_USE_PROGRAM_DEFINED_SETTING,
      useProgramDefinedSetting: state,
    })
  }

  const setAreaBearingSetting = (state: boolean) => {
    dispatch({
      type: SET_AREA_BEARING_SETTING,
      areaBearingSetting: state,
    })
  }

  const setFeatureEditMode = (editMode: boolean) => {
    if (editMode) assertDomainMutationAllowed();
    dispatch({
      type: SET_FEATURE_EDIT_MODE,
      editMode: editMode,
    })
  }

  const setDrawMode = (state: boolean) => {
    if (state) assertDomainMutationAllowed();
    dispatch({
      type: SET_DRAW_MODE,
      drawMode: state,
    })
  }

  const setMetaDataEditMode = (state: boolean) => {
    if (state) assertDomainMutationAllowed();
    dispatch({
      type: SET_META_DATA_EDIT_MODE,
      editMode: state,
    })
  }

  const setListPageTabValue = (tabValue: number) => {
    dispatch({
      type: SET_LIST_PAGE_TAB_VALUE,
      listPageTabValue: tabValue,
    })
  }

  const setTableState = (tableState: any) => {
    dispatch({
      type: SET_TABLE_STATE,
      tableState: tableState,
    })
  }

  const setBoardState = (boardState: any) => {
    dispatch({
      type: SET_BOARD_STATE,
      boardState: boardState,
    })
  }

  const setInventoryFilterIds = (ids: string[]) => {
    dispatch({
      type: SET_INVENTORY_FILTER_IDS,
      inventoryFilterIds: ids,
    })
  }

  const setEstateMapFilterFeatureIds = (filterObject: {
    [estateId: string]: { [mapId: string]: string[] | null }
  }) => {
    dispatch({
      type: SET_ESTATE_MAP_FILTER_FEATURE_IDS,
      estateMapFilterFeatureIds: filterObject,
    })
  }

  const setEstateProjectFilterActivityIds = (filterObject: {
    [estateId: string]: { [projectId: string]: string[] | null }
  }) => {
    dispatch({
      type: SET_ESTATE_PROJECT_FILTER_ACTIVITY_IDS,
      estateProjectFilterActivityIds: filterObject,
    })
  }

  const setCategories = (categories: null | string[]) => {
    dispatch({
      type: SET_CATEGORIES,
      categories: categories,
    })
  }

  const setMapCategoriesFilterLogicType = (type: CategoriesFilterLogicType) => {
    dispatch({
      type: SET_MAP_CATEGORIES_FILTER_LOGIC_TYPE,
      categoriesFilterLogicType: type,
    })
  }

  const setActiveExternalLayersLegend = (
    legends: { title: string; legends: FirestoreLayerLegend[] }[],
  ) => {
    dispatch({
      type: SET_ACTIVE_EXTERNAL_LAYERS_LEGEND,
      activeExternalLayersLegend: legends,
    })
  }

  const setExternallyActivatedThemes = (themes: string[]) => {
    dispatch({
      type: SET_EXTERNALLY_ACTIVATED_THEMES,
      externallyActivatedThemes: themes,
    })
  }

  const setNotesLayerState = (state: boolean) => {
    dispatch({
      type: SET_NOTES_LAYER_STATE,
      notesLayerActive: state,
    })
  }

  // --- Fetch data --- //
  // Activities
  const fetchActivity = (activityId: string): Promise<FirestoreActivityProps | null> => {
    return new Promise((resolve) => {
      const path = `activities/${activityId}`
      const docRef = doc(db, path)
      const unsub = onSnapshot(
        docRef,
        (activityDoc) => {
          if (!activityDoc.exists()) {
            resolve(null)
            return
          }
          const activity = { ...activityDoc.data() } as FirestoreActivityProps
          dispatch({
            type: ADDED_ACTIVITY_SUCCESS,
            payload: activity,
          })
          resolve(activity)
        },
        (error) => {
          dispatch({ type: MAP_DATA_API_CALL_FAIL, error })
          resolve(null)
        },
      )
      firestoreEventListeners.push(unsub)
    })
  }

  const fetchActivities = async (
    projectId: string,
    roleOverwrite?: MapProjectRoles,
  ) => {
    try {
      const path = `activities`
      // Set up query according to user role in project
      if (!user || !user.id) return
      // Find user role
      let userProjectRole: MapProjectRoles | '' = ''
      if (projects && projects[projectId] && projects[projectId].roles) {
        if (projects[projectId].roles![user.id]) {
          userProjectRole = projects[projectId].roles![user.id]
            .role as unknown as MapProjectRoles
        }
      }
      // Check for org-to-org subscription and set user role to admin if found
      if (
        userProjectRole !== 'admin' &&
        orgToOrgConsumerSubscriptions &&
        orgToOrgConsumerSubscriptions.length > 0
      ) {
        if (projects[projectId] && projects[projectId].estateId) {
          const orgToOgSub = orgToOrgConsumerSubscriptions.find(
            (el) => el.estateId === projects[projectId].estateId,
          )
          if (orgToOgSub) {
            userProjectRole = 'admin'
          }
        }
      }
      if (roleOverwrite) userProjectRole = roleOverwrite
      // Check if user project role is empty
      if (userProjectRole === '') return
      // Set query
      let q = query(collection(db, path), where('projectId', '==', projectId))
      if (userProjectRole !== 'admin') {
        q = query(
          collection(db, path),
          where('projectId', '==', projectId),
          where('responsible.id', '==', user.id),
        )
      }
      const unsub = onSnapshot(
        q,
        (qs) => {
          qs.docChanges().forEach((change: any) => {
            switch (change.type) {
              case 'added':
                dispatch({
                  type: ADDED_ACTIVITY_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              case 'modified':
                dispatch({
                  type: MODIFIED_ACTIVITY_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              case 'removed':
                if (state.selectedActivityId === change.doc.data().id)
                  setSelectedActivityId(null)
                dispatch({
                  type: REMOVED_ACTIVITY_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              default:
            }
          })
        },
        (error) => {
          console.info('ERROR TEST', error)
          console.trace()
        },
      )
      firestoreEventListeners.push(unsub)
    } catch (error) {
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }
  // Map features
  const fetchFeatures = async (
    id: string,
    type: 'maps' | 'activities',
    authType: 'normal' | 'anonymous',
    categories?: string[] | null,
  ) => {
    try {
      const path = `${type}/${id}/features`
      // Check for authentication type
      let q = null
      if (authType === 'anonymous') {
        // Check if audience is active on the underlaying map
        const parDoc = await getDoc(doc(db, `${type}/${id}`))
        if (!parDoc.exists() || !parDoc.data().audienceActive) {
          if (type === 'maps') {
            setSelectedMapId(null)
          } else if (type === 'activities') {
            setSelectedProjectId(null)
          }
          setSelectedFeatureId(null)
          return
        }
        q = query(
          collection(db, path),
          where('categories', 'array-contains', 'audience'),
        )
      } else {
        if (categories && categories.length > 0) {
          q = query(
            collection(db, path),
            where('categories', 'array-contains-any', categories),
          )
        } else {
          q = collection(db, path)
        }
      }
      if (!workspaceActiveRef.current) return;
      const unsub = onSnapshot(
        q,
        (qs) => {
          // const data = [] as any;
          qs.docChanges().forEach((change: any) => {
            // Try to convert data to geojson
            let newData = null
            try {
              newData = firestoreConverters.fromFirestoreFeatureToGeoJson(
                change.doc.data(),
              )
            } catch (error) {
              console.error('Error converting feature to geojson', error)
              return
            }
            let changeType = change.type
            // Filter according to private category and createdByUserId, and internal and createdByOrgId
            if (newData && newData.properties) {
              const { createdByUserId, createdByOrgId, categories } =
                newData.properties
              if (
                createdByUserId &&
                createdByUserId !== user?.id &&
                categories &&
                categories.includes('private')
              )
                changeType = 'removed'
              if (
                createdByOrgId &&
                createdByOrgId !== user?.orgId &&
                categories &&
                categories.includes('internal')
              )
                changeType = 'removed'
            }
            switch (changeType) {
              case 'added':
                dispatch({
                  type: ADDED_FEATURE_SUCCESS,
                  payload: { dataConv: newData },
                })
                break
              case 'modified':
                dispatch({
                  type: MODIFIED_FEATURE_SUCCESS,
                  payload: { dataConv: newData },
                })
                break
              case 'removed':
                dispatch({
                  type: REMOVED_FEATURE_SUCCESS,
                  payload: { dataConv: newData },
                })
                break
              default:
            }
          })
        },
        (error) => {
          console.info('ERROR TEST', error)
          console.trace()
        },
      )
      firestoreEventListeners.push(unsub)
    } catch (error) {
      console.error('Fetch error', error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }
  // Activity features
  const fetchActivityFeatures = async (activityId: string) => {
    try {
      const path = `activities/${activityId}/features`
      const q = query(collection(db, path))
      const unsub = onSnapshot(
        q,
        (qs) => {
          qs.docChanges().forEach((change: any) => {
            switch (change.type) {
              case 'added':
                dispatch({
                  type: ADDED_ACTIVITY_FEATURE_SUCCESS,
                  payload: {
                    dataConv: firestoreConverters.fromFirestoreFeatureToGeoJson(
                      change.doc.data(),
                    ),
                  },
                })
                break
              case 'modified':
                dispatch({
                  type: MODIFIED_ACTIVITY_FEATURE_SUCCESS,
                  payload: {
                    dataConv: firestoreConverters.fromFirestoreFeatureToGeoJson(
                      change.doc.data(),
                    ),
                  },
                })
                break
              case 'removed':
                dispatch({
                  type: REMOVED_ACTIVITY_FEATURE_SUCCESS,
                  payload: {
                    dataConv: firestoreConverters.fromFirestoreFeatureToGeoJson(
                      change.doc.data(),
                    ),
                  },
                })
                break
              default:
            }
          })
        },
        (error) => {
          console.info('ERROR TEST', error)
          console.trace()
        },
      )
      firestoreEventListeners.push(unsub)
    } catch (error) {
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  const fetchStandData = async (mapId: string) => {
    try {
      const path = `maps/${mapId}/stands`
      const q = query(collection(db, path), where('status', '==', 'current'))
      const unsub = onSnapshot(
        q,
        (qs) => {
          const data = [] as any
          qs.forEach((doc: any) => {
            data.push(doc.data())
          })
          dispatch({
            type: FETCH_STAND_DATA_SUCCESS,
            mapId: mapId,
            payload: data,
          })
        },
        (error) => {
          console.info('ERROR TEST', error)
          console.trace()
        },
      )
      firestoreEventListeners.push(unsub)
    } catch (error) {
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  const fetchThinningData = async (
    id: string,
    type: 'mapId' | 'projectId' | 'activityId' | 'standId',
  ) => {
    try {
      const path = `thinnings`
      // TODO:
      // let q = query(collection(db, path), where("mapId", "==", id), where("status", "!=", "deleted"));
      let q = query(collection(db, path), where('mapId', '==', id))
      if (type === 'projectId') {
        q = query(collection(db, path), where('projectId', '==', id))
      } else if (type === 'activityId') {
        q = query(collection(db, path), where('activityId', '==', id))
      } else if (type === 'standId') {
        q = query(collection(db, path), where('standId', '==', id))
      }
      const unsub = onSnapshot(
        q,
        (qs) => {
          qs.docChanges().forEach((change: any) => {
            switch (change.type) {
              case 'added':
                dispatch({
                  type: ADDED_THINNING_DATA_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              case 'modified':
                dispatch({
                  type: MODIFIED_THINNING_DATA_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              case 'removed':
                dispatch({
                  type: REMOVED_THINNING_DATA_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              default:
            }
          })
        },
        (error) => {
          console.info('ERROR TEST', error)
          console.trace()
        },
      )
      firestoreEventListeners.push(unsub)
    } catch (error) {
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  const fetchNotes = async (mapId: string) => {
    try {
      const path = `maps/${mapId}/notes`
      const q = query(collection(db, path))
      const unsub = onSnapshot(
        q,
        (qs) => {
          qs.docChanges().forEach((change: any) => {
            switch (change.type) {
              case 'added':
                dispatch({
                  type: ADDED_NOTE_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              case 'modified':
                dispatch({
                  type: MODIFIED_NOTE_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              case 'removed':
                dispatch({
                  type: REMOVED_NOTE_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              default:
            }
          })
        },
        (error) => {
          console.info('ERROR TEST', error)
          console.trace()
        },
      )
      firestoreEventListeners.push(unsub)
    } catch (error) {}
  }
  // --- Activities --- //
  const saveActivity = async (
    props: SaveActivityProps,
  ): Promise<string | null> => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    try {
      const { id, projectId, dontCheckForMapFeatures, thinningData, payload } =
        props
      let docId: string
      let docRef: DocumentReference
      if (!projectId) throw new Error('No project id found!!!')
      if (!id) {
        // Create document path string
        docRef = doc(collection(db, 'activities'))
        docId = docRef.id
        await setDoc(docRef, {
          ...dataObjectWithoutUndefinedFields(payload),
          projectId: projectId,
          id: docId,
          status: 'current',
          creationDate: dayjs().format(),
          lastModifiedDate: dayjs().format(),
          lastModifiedBy: {
            id: user?.id || '',
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            email: user?.email || '',
          },
        })
        // Log event in mixpanel
        callCloudFunction('trackEvent', {
          event: mixpanelEvents.activityCreated,
        })
      } else {
        docRef = doc(db, `activities/${id}`)
        docId = id
        await updateDoc(docRef, {
          ...dataObjectWithoutUndefinedFields(payload),
          projectId: projectId,
          lastModifiedDate: dayjs().format(),
          lastModifiedBy: {
            id: user?.id || '',
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            email: user?.email || '',
          },
        })
        // Log event in mixpanel
        callCloudFunction('trackEvent', {
          event: mixpanelEvents.activityUpdated,
        })
      }
      // Go through mapFeatureRefs and save features under activity as origins and editable features
      const { mapFeatureRefs, correlationId } = payload
      if (
        !dontCheckForMapFeatures &&
        mapFeatureRefs &&
        mapFeatureRefs.length > 0
      ) {
        mapFeatureRefs.forEach(async (el: string) => {
          const splitString = el.split('/')
          const mapId = splitString[0]
          const featureId = splitString[1]
          let skipOriginsFeature = false
          let originsFeatureMetric = null
          // Set feature from reducer state
          let feat = state.features[mapId][featureId]
          let originsFeat = { ...feat }

          // Check if ediable feature already exist under activity with originsFeatureId
          if (state.activityFeatures[docId]) {
            const tt = Object.values(state.activityFeatures[docId]).find(
              (el: any) => {
                return el.properties.originsFeatureId === featureId
              },
            )
            if (tt) {
              skipOriginsFeature = true
            }
          }
          // Check if origins feature already exist under activity
          const featPath = `activities/${docId}/features/${featureId}`
          const featDocRef = doc(db, featPath)
          const featDocSnap = await getDoc(featDocRef)
          const docExists = featDocSnap.exists()
          // Set/update origins feature
          if (!skipOriginsFeature && originsFeat) {
            originsFeat.properties = {
              ...originsFeat.properties,
              activityId: docId,
              originFeature: true,
              correlationId: correlationId,
            }
            originsFeatureMetric = originsFeat.properties.featureMetric
            await saveFeature(
              {
                id: featureId,
                activityId: docId,
                payload: originsFeat,
              },
              'activities',
            )
          }
          // If feature does not exist under activity also add editable feature
          if (!docExists && !skipOriginsFeature) {
            let editableFeat = { ...feat, properties: { ...(feat.properties ?? {}) } }
            // If feature is a line or polygon set style
            let newStyleObject = { ...editableFeat.properties.styleObject }
            if (
              [
                'LineString',
                'MultiLineString',
                'Polygon',
                'MultiPolygon',
              ].includes(feat.geometry.type)
            ) {
              // Get feature color based on biomassScreening
              const newColorId = getDefaultActivityColorId({
                activity: payload as FirestoreActivityProps,
                referencedToMap: true,
              })
              newStyleObject = {
                fillColor: newColorId,
                fillOpacity: 0,
                lineColor: newColorId,
                lineStyle: 'Solid',
                lineWidth: 3,
                polygonStyle: 'Filled',
              }
            }
            if (editableFeat) {
              editableFeat.properties = {
                ...editableFeat.properties,
                id: null,
                activityId: docId,
                originsFeatureId: featureId,
                originsFeatureMetric: originsFeatureMetric,
                countInMetrics: true,
                originFeature: false,
                mainSpecies: '',
                mainSpeciesYear: '',
                section: [],
                coverage: null,
                areaBearing: false,
                title: payload.name ? payload.name : '',
                styleObject: newStyleObject,
                correlationId: correlationId,
              }
              await saveFeature(
                {
                  activityId: docId,
                  payload: editableFeat,
                },
                'activities',
              )
            }
          }
        })
      }
      // Save possible thinning data
      if (thinningData && thinningData.length > 0) {
        thinningData.forEach(async (thinning) => {
          const {
            mapId,
            projectId,
            standId,
            thinningId,
            featureId,
            disabled,
            thinningPercentage,
          } = thinning
          if (thinningPercentage > 0 || !thinningId.startsWith('new-')) {
            await saveThinning({
              id: thinningId,
              mapId,
              projectId,
              featureId,
              activityId: docId,
              standId,
              usedInProjection: disabled !== undefined ? disabled : false,
              percentageThinning: thinningPercentage,
              executionDate: payload.expireDate ? payload.expireDate : '',
              executed: payload.activityStatus === 'done' ? true : false,
              createdByUserId: user?.id,
              correlationId: correlationId,
            })
          }
        })
      }
      // Set selected activity id if different from last saved activity
      if (state.selectedActivityId !== docId) setSelectedActivityId(docId)
      dispatch({ type: SAVE_ACTIVITY_SUCCESS })
      return docId
    } catch (error) {
      console.error(error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
    return null
  }

  const updateActivityStatus = async (
    id: string,
    status: DEFAULT_ACTIVITY_STATUS_TYPE,
  ) => {
    assertDomainMutationAllowed();
    try {
      const path = `activities/${id}`
      const docRef = doc(db, path)
      await updateDoc(docRef, {
        activityStatus: status,
        lastModifiedDate: dayjs().format(),
        lastModifiedBy: {
          id: user?.id || '',
          name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
          email: user?.email || '',
        },
      })
    } catch (error) {
      console.error(error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  const updateActivityPriority = async (id: string, priority: number) => {
    assertDomainMutationAllowed();
    try {
      const path = `activities/${id}`
      const docRef = doc(db, path)
      await updateDoc(docRef, {
        priority: priority,
        lastModifiedDate: dayjs().format(),
        lastModifiedBy: {
          id: user?.id || '',
          name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
          email: user?.email || '',
        },
      })
    } catch (error) {
      console.error(error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  const deleteActivity = async (id: string) => {
    assertDomainMutationAllowed();
    try {
      if (state.selectedActivityId === id) setSelectedActivityId(null)

      // 1) Run all registered file handlers
      const handlers = Array.from(registry.current.values())
      if (handlers.length > 0) {
        const results = await Promise.allSettled(handlers.map((h) => h(id)))
        const rejected = results.filter((r) => r.status === 'rejected')
        if (rejected.length > 0) {
          const reasons = rejected.map(
            (r) => (r as PromiseRejectedResult).reason,
          )
          console.error(
            'deleteActivity aborted — one or more file deletions failed:',
            reasons,
          )
          throw new Error(
            'Failed to delete files for activity. Aborting activity delete.',
          )
        }
      }
      // 2) Delete activity document (all subcollections will be deleted through firestore rules)
      const path = `activities/${id}`
      const docRef = doc(db, path)
      assertDomainMutationAllowed();
      await deleteDoc(docRef)
      // Log event in mixpanel
      callCloudFunction('trackEvent', { event: mixpanelEvents.activityDeleted })
    } catch (error) {
      console.error(error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  // --- Features --- //
  // Save feature
  const saveFeature = async (
    props: SaveFeatureProps,
    type: 'maps' | 'activities',
  ) => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    // Convert data
    let properties: any = {}
    if (props.payload) {
      const data = firestoreConverters.fromGeoJsonFeatureToFirestore(
        props.payload,
      )
      // TODO : we add activity type to the feature properties,
      // when rendering them in the map, but we most remove them before saving
      const {
        creationDate,
        lastModifiedDate,
        id,
        activityType,
        ...remainderProps
      } = data.firestoreFeature
      properties = remainderProps
    }
    if (props.correlationId && !properties.correlationId) {
      properties.correlationId = props.correlationId
    }
    // Check for parent id
    let parId = props.mapId
    if (type === 'activities') parId = props.activityId
    if (!parId) throw new Error('No parent id found!!!')
    try {
      let id = props.id
      if (!id) {
        // Create document path string
        const path = `${type}/${parId}/features`
        const docRef = doc(collection(db, path))
        id = docRef.id
        await setDoc(docRef, {
          ...dataObjectWithoutUndefinedFields(properties),
          creationDate: dayjs().format(),
          lastModifiedDate: dayjs().format(),
          mapId: props.mapId ? parId : null,
          activityId: props.activityId ? parId : null,
          createdByUserId: user?.id,
          createdByOrgId: user?.orgId,
          id: id,
          status: 'current',
          ...(type === 'maps' ? { includeInAudience: includeFeatureInAudience({ ...properties, status: 'current' }) } : {}),
          lastModifiedBy: {
            id: user?.id || '',
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            email: user?.email || '',
          },
        })
      } else {
        const path = `${type}/${parId}/features/${props.id}`
        const docRef = doc(db, path)
        // Needed because when creating an activity we copy the original feature to the activity features
        // and then the feauture already has an id
        const docSnap = await getDoc(docRef)
        if (!docSnap.exists()) {
          assertDomainMutationAllowed();
          await setDoc(docRef, {
            ...dataObjectWithoutUndefinedFields(properties),
            id: id,
            lastModifiedDate: dayjs().format(),
            createdByUserId: properties.createdByUserId
              ? properties.createdByUserId
              : user?.id,
            createdByOrgId: properties.createdByOrgId
              ? properties.createdByOrgId
              : user?.orgId,
            status: 'current',
            ...(type === 'maps' ? { includeInAudience: includeFeatureInAudience({ ...properties, status: 'current' }) } : {}),
            lastModifiedBy: {
              id: user?.id || '',
              name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
              email: user?.email || '',
            },
          })
        } else {
          assertDomainMutationAllowed();
          await updateDoc(docRef, {
            ...dataObjectWithoutUndefinedFields(properties),
            ...(type === 'maps' ? { includeInAudience: includeFeatureInAudience({ ...docSnap.data(), ...dataObjectWithoutUndefinedFields(properties) }) } : {}),
            lastModifiedDate: dayjs().format(),
            createdByUserId: properties.createdByUserId
              ? properties.createdByUserId
              : user?.id,
            createdByOrgId: properties.createdByOrgId
              ? properties.createdByOrgId
              : user?.orgId,
            lastModifiedBy: {
              id: user?.id || '',
              name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
              email: user?.email || '',
            },
          })
        }
      }
      dispatch({
        type: SAVE_FEATURE_SUCCESS,
        lastSavedFeature: {
          id: id as string,
          geometry: props.payload.geometry,
          entityType: "feature",
        },
      })
    } catch (error) {
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  const saveCutTransaction = async ({ sourceWrites, cutterWrite }: SaveCutTransactionProps) => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    try {
      const batch = writeBatch(db)
      const now = dayjs().format()
      const writeFeature = (write: SaveCutTransactionProps['cutterWrite']) => {
        if (!write.parentId) throw new Error('No parent id found for cut transaction')
        const docRef = write.id
          ? doc(db, `${write.type}/${write.parentId}/features/${write.id}`)
          : doc(collection(db, `${write.type}/${write.parentId}/features`))
        if (write.delete) {
          batch.delete(docRef)
          return null
        }
        if (!write.payload) throw new Error('Missing feature payload for cut transaction')
        const payload = {
          ...write.payload,
          properties: {
            ...(write.payload.properties ?? {}),
            mapId: write.type === 'maps' ? write.parentId : null,
            activityId: write.type === 'activities' ? write.parentId : null,
          },
        }
        const data = firestoreConverters.fromGeoJsonFeatureToFirestore(payload)
        const { creationDate, lastModifiedDate, id, activityType, ...properties } = data.firestoreFeature
        const featureId = write.id ?? docRef.id
        const documentData = {
          ...dataObjectWithoutUndefinedFields(properties),
          id: featureId,
          mapId: write.type === 'maps' ? write.parentId : null,
          activityId: write.type === 'activities' ? write.parentId : null,
          status: 'current',
          ...(write.type === 'maps' ? { includeInAudience: includeFeatureInAudience({ ...properties, status: 'current' }) } : {}),
          lastModifiedDate: now,
          lastModifiedBy: {
            id: user?.id || '',
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            email: user?.email || '',
          },
          ...(!write.id
            ? {
                creationDate: now,
                createdByUserId: user?.id,
                createdByOrgId: user?.orgId,
              }
            : {}),
        }
        // `set(..., { merge: true })` deep-merges maps. That corrupts a
        // Polygon -> MultiPolygon conversion by retaining the old
        // `coordinates.perimeter` / `coordinates.holes` alongside the new
        // numeric multipolygon keys. Existing documents must therefore use
        // an update, which replaces the complete coordinates map.
        if (write.id) batch.update(docRef, documentData)
        else batch.set(docRef, documentData)
        return featureId
      }
      sourceWrites.forEach(writeFeature)
      const cutterFeatureId = writeFeature(cutterWrite)
      await batch.commit()
      dispatch({
        type: SAVE_FEATURE_SUCCESS,
        lastSavedFeature: {
          id: cutterFeatureId,
          geometry: cutterWrite.payload?.geometry ?? null,
          entityType: "feature",
        },
      })
    } catch (error) {
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error })
      throw error
    }
  }

  // Delete feature
  const deleteFeature = async (
    props: DeleteFeatureProps,
    type: 'maps' | 'activities',
    correlationId?: string,
  ) => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    try {
      if (state.selectedFeatureId === props.id) setSelectedFeatureId(null)
      let parId = props.mapId
      if (type === 'activities') parId = props.activityId
      if (!parId) throw new Error('No parent id found!!!')
      const path = `${type}/${parId}/features/${props.id}`
      // Check for correlationId and update doc before deleting
      if (correlationId) {
        const docRef = doc(db, path)
        await updateDoc(docRef, {
          correlationId: correlationId,
          lastModifiedDate: dayjs().format(),
          lastModifiedBy: {
            id: user?.id || '',
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            email: user?.email || '',
          },
        })
      }
      assertDomainMutationAllowed();
      await deleteDoc(doc(db, path))
      // If type is activities also delete origins feature
      if (props.originsFeatureId && type === 'activities') {
        const orgPath = `${type}/${parId}/features/${props.originsFeatureId}`
        if (correlationId) {
          const docRef = doc(db, orgPath)
          assertDomainMutationAllowed();
          await updateDoc(docRef, {
            correlationId: correlationId,
            lastModifiedDate: dayjs().format(),
            lastModifiedBy: {
              id: user?.id || '',
              name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
              email: user?.email || '',
            },
          })
        }
        assertDomainMutationAllowed();
        await deleteDoc(doc(db, orgPath))
      }
      dispatch({ type: DELETE_FEATURE_SUCCESS })
    } catch (error) {
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  // --- Stand data --- //
  const saveStandData = async (props: SaveStandDataProps) => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    try {
      // Fetch feature information
      const featPath = `maps/${props.mapId}/features/${props.featureId}`
      const featDocRef = doc(db, featPath)
      const featDocSnap = await getDoc(featDocRef)
      // Check that feature doc exist on db
      if (!featDocSnap.exists())
        throw new Error(
          'Not possible to save stand data on non-existing feature!!!',
        )
      let featData = { ...featDocSnap.data() }
      let newStandIds = featData.standIds ? featData.standIds : []
      // run through each stand data
      props.payload.forEach(async (el, idx) => {
        // Check for new stand data
        if (el.id.startsWith('new-')) {
          const path = `maps/${props.mapId}/stands`
          const docRef = doc(collection(db, path))
          const standId = docRef.id
          // Insert id in feature standIds array
          if (idx === 0) {
            newStandIds.unshift(standId)
          } else {
            newStandIds.push(standId)
          }
          assertDomainMutationAllowed();
          await setDoc(docRef, {
            ...dataObjectWithoutUndefinedFields(el),
            creationDate: dayjs().format(),
            lastModifiedDate: dayjs().format(),
            mapId: props.mapId,
            featureId: props.featureId,
            id: standId,
            status: 'current',
            correlationId: props.correlationId ? props.correlationId : null,
            lastModifiedBy: {
              id: user?.id || '',
              name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
              email: user?.email || '',
            },
          })
        } else {
          // EXISTING DOC. TODO : create revision
          // Update existing stand data
          const path = `maps/${props.mapId}/stands/${el.id}`
          const docRef = doc(db, path)
          // Check if stand has been deleted
          if (el.status === 'deleted') {
            // Remove stand id from feature standIds array
            const standIdx = newStandIds.indexOf(el.id)
            if (standIdx !== -1) newStandIds.splice(standIdx, 1)
            // Delete stand data from firestore
            assertDomainMutationAllowed();
            await deleteDoc(docRef)
            return
          } else {
            // Check if stand id is in feature
            const standIdx = newStandIds.indexOf(el.id)
            if (standIdx === -1) {
              if (idx === 0) newStandIds = [el.id, ...newStandIds]
            } else {
              if (idx === 0 && standIdx !== 0) {
                newStandIds = [el.id, ...newStandIds.splice(standIdx, 1)]
              }
            }
          }
          assertDomainMutationAllowed();
          updateDoc(docRef, {
            ...dataObjectWithoutUndefinedFields(el),
            lastModifiedDate: dayjs().format(),
            lastModifiedBy: {
              id: user?.id || '',
              name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
              email: user?.email || '',
            },
            correlationId: props.correlationId ? props.correlationId : null,
          })
        }
      })
      // Update feature standIds array in db
      assertDomainMutationAllowed();
      updateDoc(featDocRef, {
        mainSpecies: props.payload[0].species,
        mainSpeciesYear: props.payload[0].year ? props.payload[0].year : '',
        standIds: newStandIds,
        lastModifiedDate: dayjs().format(),
        lastModifiedBy: {
          id: user?.id || '',
          name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
          email: user?.email || '',
        },
        correlationId: props.correlationId ? props.correlationId : null,
      })

      dispatch({ type: SAVE_STAND_DATA_SUCCESS })
    } catch (error) {
      console.error(error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  // Save temp stand update data
  const saveTempStandUpdateData = async (
    props: SaveTempStandUpdateDataProps,
  ) => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    try {
      const path = `maps/${props.mapId}/tempStandUpdateData`
      const docRef = doc(collection(db, path))
      await setDoc(docRef, {
        stands: props.payload,
        creationDate: dayjs().format(),
        lastModifiedDate: dayjs().format(),
        jobId: props.jobId,
        id: docRef.id,
      })
      // Log event in mixpanel
      dispatch({ type: SAVE_STAND_DATA_SUCCESS })
    } catch (error) {
      console.error(error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  // Save projection data
  const saveProjectionData = async (props: saveProjectionDataProps) => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    try {
      const path = `maps/${props.mapId}/projections`
      const docRef = doc(collection(db, path))
      const { totalArea, projections } = props.payload
      await setDoc(docRef, {
        projections: { ...dataObjectWithoutUndefinedFields(projections) },
        totalArea: totalArea ? totalArea : 0,
        creationDate: dayjs().format(),
        lastModifiedDate: dayjs().format(),
        id: docRef.id,
        lastModifiedBy: {
          id: user?.id || '',
          name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
          email: user?.email || '',
        },
      })
      dispatch({ type: SAVE_STAND_DATA_SUCCESS })
    } catch (error) {
      console.error(error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  // --- Thinning --- //
  const saveThinning = async (props: SaveThinningProps) => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    try {
      // Check for new thinning
      if (props.id.startsWith('new-') && props.percentageThinning !== 0) {
        const docRef = doc(collection(db, 'thinnings'))
        const id = docRef.id
        await setDoc(docRef, {
          ...dataObjectWithoutUndefinedFields(props),
          creationDate: dayjs().format(),
          lastModifiedDate: dayjs().format(),
          status: 'current',
          id: id,
          lastModifiedBy: {
            id: user?.id || '',
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            email: user?.email || '',
          },
        })
      } else {
        if (props.percentageThinning === 0) {
          await deleteDoc(doc(db, `thinnings/${props.id}`))
        } else {
          await updateDoc(doc(db, `thinnings/${props.id}`), {
            ...dataObjectWithoutUndefinedFields(props),
            lastModifiedDate: dayjs().format(),
            lastModifiedBy: {
              id: user?.id || '',
              name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
              email: user?.email || '',
            },
          })
        }
      }
      dispatch({ type: SAVE_THINNING_DATA_SUCCESS })
    } catch (error) {
      console.error(error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  const deleteThinning = async (id: string) => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    try {
      await deleteDoc(doc(db, `thinnings/${id}`))
      dispatch({ type: DELETE_THINNING_DATA_SUCCESS })
    } catch (error) {
      console.error(error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  // --- Inventory --- //
  // Save invenrtory
  const saveInventory = async (
    data: SaveInventoryItemProps,
    id?: string,
  ): Promise<string | undefined> => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    try {
      let docId = id
      if (docId) {
        // Update inventory data
        const docRef = doc(db, `inventories/${docId}`)
        await runTransaction(db, async (transaction) => {
          const snapshot = await transaction.get(docRef);
          if (!snapshot.exists()) throw new Error("inventory-stale");
          const current = snapshot.data() as FirestoreInventoryItemProps;
          if (inventoryLocked(current)) throw new Error("inventory-stale");
          if (data.lastModifiedDate !== undefined) assertInventoryCurrent(data, current);
          const update: any = dataObjectWithoutUndefinedFields(data);
          if (Object.prototype.hasOwnProperty.call(data, "buyer")) {
            update.buyer = preserveApproval(data.buyer, current.buyer);
          }
          assertDomainMutationAllowed();
          transaction.update(docRef, {
            ...update,
            lastModifiedDate: new Date().toISOString(),
            lastModifiedBy: {
              id: user?.id || '',
              name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
              email: user?.email || '',
            },
          });
        })
        // Set additional properties
        let properties = {
          buyerApprovalStatus: getInventoryBuyerApprovalStatus(data.buyer),
          inventoryType: data.inventoryType ?? 'unknown',
          inventoryStatus: data.inventoryStatus ?? 'unknown',
          uniqueInventoryId: data.uniqueInventoryId ?? 'unknown',
        }
        // Log event in mixpanel
        callCloudFunction('trackEvent', {
         event: mixpanelEvents.inventoryUpdated,
         properties: properties,
       })
      } else {
        // Create inventory document
        const docRef = doc(collection(db, 'inventories'))
        docId = docRef.id
        await setDoc(docRef, {
          ...dataObjectWithoutUndefinedFields(data),
          id: docId,
          status: 'current',
          inventoryStatus: 'forwarded',
          creationDate: dayjs().format(),
          lastModifiedDate: dayjs().format(),
          lastModifiedBy: {
            id: user?.id || '',
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            email: user?.email || '',
          },
          orgId: user?.orgId,
        })
        // Log event in mixpanel
        let properties = {
          buyerApprovalStatus: getInventoryBuyerApprovalStatus(data.buyer),
          inventoryType: data.inventoryType ?? 'unknown',
          inventoryStatus: data.inventoryStatus ?? 'unknown',
          uniqueInventoryId: data.uniqueInventoryId ?? 'unknown',
        }
        callCloudFunction('trackEvent', {
          event: mixpanelEvents.inventoryCreated,
          properties: properties,
        })
      }
      // Save inventory item as last saved feature in reducer if coordinates are there
      if (data.coordinates) {
        const geometry: Geometry = {
          type: 'Point',
          coordinates: [data.coordinates!.lng, data.coordinates!.lat],
        }
        dispatch({
          type: SAVE_FEATURE_SUCCESS,
          lastSavedFeature: { id: docId as string, geometry, entityType: "inventory" },
        })
      }
      dispatch({ type: MAP_DATA_API_CALL_SUCCESS })
      return docId
    } catch (error) {
      console.error('Error saving inventory', error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  const deleteInventory = async (
    id: string,
    uniqueInventoryId: number,
    inventoryIdGroup: FirestoreInventoryItemProps['inventoryIdGroup'],
    correlationId?: string,
  ) => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    try {
      setSelectedFeatureId(null)
      // Set inventory to deleted status
      const docRef = doc(db, `inventories/${id}`)
      await withEditableInventory(id, async (transaction) => {
        assertDomainMutationAllowed();
        return transaction.update(docRef, {
          status: 'deleted',
          lastModifiedDate: dayjs().format(),
          correlationId: correlationId || null,
          lastModifiedBy: {
            id: user?.id || '',
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            email: user?.email || '',
          },
        });
      })
      // Log event in mixpanel
      callCloudFunction('trackEvent', {
        event: mixpanelEvents.inventoryDeleted,
      })
      // Release unique id
      await callCloudFunction('releaseReservedId', {
        orgId: user?.orgId,
        id: uniqueInventoryId,
        group: inventoryIdGroup,
      })
      dispatch({ type: MAP_DATA_API_CALL_SUCCESS })
    } catch (error) {
      console.error('Error deleting inventory', error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  const saveInventoryMeasurement = async (
    data: SaveInventoryMeasurementProps,
    id?: string,
  ) => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    const inventoryId = data.inventoryId
    if (!inventoryId) {
      console.error('Inventory ID is required for saving inventory measurement')
      dispatch({
        type: MAP_DATA_API_CALL_FAIL,
        error: 'Inventory ID is required',
      })
      return
    }
    try {
      const parentRef = doc(db, `inventories/${inventoryId}`);
      const measurementRef = id ? doc(db, `inventories/${inventoryId}/measurements/${id}`)
        : doc(collection(db, `inventories/${inventoryId}/measurements`));
      await runTransaction(db, async (transaction) => {
        const parent = await transaction.get(parentRef);
        if (!parent.exists() || inventoryLocked(parent.data())) throw new Error("inventory-stale");
        if (id) {
          const measurement = await transaction.get(measurementRef);
          if (!measurement.exists() || (data.lastModifiedDate !== undefined &&
            data.lastModifiedDate !== measurement.data().lastModifiedDate)) throw new Error("inventory-stale");
        }
        const date = new Date().toISOString();
        const actor = { id: user?.id || '', name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(), email: user?.email || '' };
        const values = { ...dataObjectWithoutUndefinedFields(data), lastModifiedDate: date, lastModifiedBy: actor };
        assertDomainMutationAllowed();
        transaction.update(parentRef, { lastModifiedDate: date, lastModifiedBy: actor });
        if (id) transaction.update(measurementRef, values);
        else transaction.set(measurementRef, { ...values, id: measurementRef.id, inventoryId, creationDate: date, status: 'current' });
      });
      callCloudFunction('trackEvent', { event: id ? mixpanelEvents.inventoryMeasurementUpdated : mixpanelEvents.inventoryMeasurementCreated });
      dispatch({ type: MAP_DATA_API_CALL_SUCCESS })
    } catch (error) {
      console.error('Error saving inventory measurement', error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  const fetchInventories = async (
    id: string,
    type: 'orgId' | 'estateId' | 'mapId' | 'projectId' | 'buyerId',
  ) => {
    // Set up query
    let q = null
    if (type === 'orgId') {
      q = query(
        collection(db, 'inventories'),
        where('orgId', '==', id),
        where('status', '!=', 'deleted'),
      )
    } else if (type === 'estateId') {
      q = query(
        collection(db, 'inventories'),
        where('origins.estateId', '==', id),
        where('status', '!=', 'deleted'),
      )
    } else if (type === 'mapId') {
      q = query(
        collection(db, 'inventories'),
        where('origins.mapId', '==', id),
        where('status', '!=', 'deleted'),
      )
    } else if (type === 'projectId') {
      q = query(
        collection(db, 'inventories'),
        where('origins.projectId', '==', id),
        where('status', '!=', 'deleted'),
      )
    } else if (type === 'buyerId') {
      q = query(
        collection(db, 'inventories'),
        where('buyer.id', '==', id),
        where('buyer.releasedToBuyer', '==', true),
        where('status', '!=', 'deleted'),
      )
    }
    if (!q) return
    // Run query
    try {
      const unsub = onSnapshot(
        q,
        (qs) => {
          qs.docChanges().forEach((change: any) => {
            switch (change.type) {
              case 'added':
                // Fetch associated measurements
                fetchInventoryMeasurements(change.doc.id)
                // Check for buyer type
                const inventoryData = change.doc.data()
                if (
                  type === 'buyerId' &&
                  user?.id &&
                  inventoryData.orgId !== inventoryData.buyer.id
                ) {
                  // unpack origins data
                  const { origins } = inventoryData
                  if (!origins) return
                  const { projectId, activityId } = origins
                  if (!projectId || !activityId) return
                  // Set up fetch of project and activity data
                  fetchTempProjectWithRole(projectId, 'buyer', user.id)
                  fetchActivity(activityId)
                }
                // Dispatch added inventory action
                dispatch({
                  type: ADDED_INVENTORY_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              case 'modified':
                dispatch({
                  type: MODIFIED_INVENTORY_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              case 'removed':
                dispatch({
                  type: REMOVED_INVENTORY_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              default:
            }
          })
        },
        (error) => {
          console.info('ERROR TEST', error)
        },
      )
      firestoreEventListeners.push(unsub)
      dispatch({ type: MAP_DATA_API_CALL_SUCCESS })
    } catch (error) {
      console.error('Error fetching inventories', error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  const fetchInventoryMeasurements = async (inventoryId: string) => {
    try {
      const path = `inventories/${inventoryId}/measurements`
      const q = query(collection(db, path))
      const unsub = onSnapshot(
        q,
        (qs) => {
          qs.docChanges().forEach((change: any) => {
            switch (change.type) {
              case 'added':
                dispatch({
                  type: ADDED_INVENTORY_MEASUREMENT_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              case 'modified':
                dispatch({
                  type: MODIFIED_INVENTORY_MEASUREMENT_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              case 'removed':
                dispatch({
                  type: REMOVED_INVENTORY_MEASUREMENT_SUCCESS,
                  payload: { ...change.doc.data() },
                })
                break
              default:
            }
          })
        },
        (error) => {
          console.info('ERROR TEST', error)
        },
      )
      firestoreEventListeners.push(unsub)
      dispatch({ type: MAP_DATA_API_CALL_SUCCESS })
    } catch (error) {
      console.error('Error fetching inventory measurements', error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  // --- Notes --- //
  const saveNote = async (props: SaveNoteProps) => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    try {
      if (props.id.startsWith('new-')) {
        const path = `maps/${props.mapId}/notes`
        const docRef = doc(collection(db, path))
        await setDoc(docRef, {
          ...dataObjectWithoutUndefinedFields(props),
          creationDate: dayjs().format(),
          lastModifiedDate: dayjs().format(),
          createdById: user?.id,
          id: docRef.id,
          lastModifiedBy: {
            id: user?.id || '',
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            email: user?.email || '',
          },
        })
        // Log event in mixpanel
        callCloudFunction('trackEvent', { event: mixpanelEvents.noteCreated })
      } else {
        const path = `maps/${props.mapId}/notes/${props.id}`
        const docRef = doc(db, path)
        await updateDoc(docRef, {
          ...dataObjectWithoutUndefinedFields(props),
          lastModifiedDate: dayjs().format(),
          lastModifiedBy: {
            id: user?.id || '',
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            email: user?.email || '',
          },
        })
      }
      dispatch({ type: SAVE_STAND_DATA_SUCCESS })
    } catch (error) {
      console.error(error)
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  const deleteNote = async ({ mapId, id }: DeleteNoteProps) => {
    assertDomainMutationAllowed();
    dispatch({ type: MAP_DATA_API_CALL_START })
    try {
      const path = `maps/${mapId}/notes/${id}`
      await deleteDoc(doc(db, path))
      // Log event in mixpanel
      callCloudFunction('trackEvent', { event: mixpanelEvents.noteDeleted })
      dispatch({ type: DELETE_FEATURE_SUCCESS })
    } catch (error) {
      dispatch({ type: MAP_DATA_API_CALL_FAIL, error: error })
    }
  }

  return (
    <MapDataContext.Provider
      value={{
        ...state,
        ...(isAudienceMode ? { editMode: false, drawMode: false, metaDataEditMode: false } : {}),
        setTriggerRerender,
        setSelectedFeatureId,
        setSelectedFeature,
        setSelectedActivityId,
        setRemoveFeatureId,
        setAddStandData,
        setAddMeasurementData,
        setUseProgramDefinedSetting,
        setAreaBearingSetting,
        setFeatureEditMode,
        setDrawMode,
        setMetaDataEditMode,
        setListPageTabValue,
        setTableState,
        setBoardState,
        setInventoryFilterIds,
        setEstateMapFilterFeatureIds,
        setEstateProjectFilterActivityIds,
        setCategories,
        setMapCategoriesFilterLogicType,
        setActiveExternalLayersLegend,
        setExternallyActivatedThemes,
        setNotesLayerState,
        fetchActivity,
        fetchActivities,
        fetchActivityFeatures,
        fetchFeatures,
        fetchStandData,
        fetchThinningData,
        fetchInventoryMeasurements,
        saveActivity,
        updateActivityStatus,
        updateActivityPriority,
        deleteActivity,
        saveFeature,
        saveCutTransaction,
        deleteFeature,
        saveStandData,
        saveTempStandUpdateData,
        saveProjectionData,
        saveThinning,
        deleteThinning,
        saveInventory,
        getInventoryEditorSnapshot,
        saveInventoryEditor: (props) => saveInventoryEditor(props, assertDomainMutationAllowed),
        deleteInventory,
        saveInventoryMeasurement,
        saveNote,
        deleteNote,
        registerFileHandler,
        unregisterFileHandler,
      }}
    >
      {children}
    </MapDataContext.Provider>
  )
}

export default MapDataContext
